import React from "react";
import CommercialLayout from "../../../components/CommercialLayout";
import { useStores } from "../../../context/storesContext";
import CommercialContentPage from "../../../components/CommercialContentPage";
import useSWR from "swr";
import { Range } from "../../../utils/range";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import { TurnoverObjective } from "../../../types/commercial";
import { EcomServiceType } from "../../../utils/ecomServiceType";
import { useSearchParams } from "react-router-dom";
import useBreakPoint from "../../../utils/useBreakPoint";
import CommercialContentPageDesktop from "../../../components/CommercialContentPageDesktop";
import { isEmailInWhitelist } from "../../../utils/whiteListUtils";
import { useAuth } from "../../../Auth/AuthProvider";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import { StoreType } from "../../../types/store";
import useLandscape from "../../../utils/useLandscape";

function Live() {
  const { user } = useAuth();
  const { selectedStore } = useStores();
  const [searchParams] = useSearchParams();
  const { isKpiExists } = useKpiVisibility();
  const currentView = useBreakPoint();
  const isLandscape = useLandscape();
  const isDesktopOrTablet =
    currentView === "desktop" ||
    currentView === "tablet" ||
    (currentView === "phone" && isLandscape);
  const displayLegacyUi = !isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) || !isDesktopOrTablet;

  const byHour = (searchParams.get("byHour") ?? "SDV").replace(/\s+/g, '_');

  // live turnover
  const { data: directTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.Direct}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
  // past turnover
  const { data: pastTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.DirectLastYear}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
  //objective
  const { data: directObjectiveTurnoverData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey
        ? [
            `/commercial/objective?range=${Range.Direct}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // live Drive turnover
  const { data: directDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.Direct}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // past Drive turnover
  const { data: pastDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.DirectLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`,  
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  // Direct sector turnover
  const { data: directSectorData, error: directSectorError } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverBySector") && displayLegacyUi
        ? [
            `/commercial/sector?range=${
              Range.Direct
            }&storeType=${selectedStore?.storeType?.toUpperCase()}&sector=${"SDV"}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );

  // Direct turnoverByHour
  const { data: directTurnoverByHourData, error: directTurnoverByHourError } =
    useSWR(
      () =>
        selectedStore?.storeKey && byHour && isKpiExists("Commercial", "TurnoverByHour") && displayLegacyUi
          ? [
              `/commercial/turnoverByHour?range=${Range.Direct}&sector=${byHour}&storeType=${selectedStore?.storeType?.toUpperCase()}`,
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );

  // LAST WEEK turnoverByHour
  const {
    data: lastWeekturnoverByHourData,
    error: lastWeekturnoverByHourError,
  } = useSWR(
    () =>
      selectedStore?.storeKey && byHour && isKpiExists("Commercial", "TurnoverByHour") && displayLegacyUi
        ? [
            `/commercial/turnoverByHour?range=${Range.LastWeek}&sector=${byHour}&storeType=${selectedStore?.storeType?.toUpperCase()}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  // objective DRIVE
  const { data: currentDriveObjectiveData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/objective?range=${Range.Direct}&ecomServiceType=${EcomServiceType.Drive}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  const contentPageProps = {
    range: Range.Direct,
    hasComparative: false,
    hasTurnoverByHour: true,
    storeType: (selectedStore?.storeType) as StoreType,
    currentTurnoverPromoData: { disabled: true },
    currentMemberCardTurnoverData: { disabled: true },
    byHourLastYear: lastWeekturnoverByHourData,
    byHourToday: directTurnoverByHourData,
    currentDriveData: directDriveTurnoverData,
    currentTurnoverData: directTurnoverData,
    driveObjective: currentDriveObjectiveData,
    pastDriveData: pastDriveTurnoverData,
    pastTurnoverData: pastTurnoverData,
    pastTurnoverPromoData: { disabled: true },
    pastMemberCardTurnoverData: { disabled: true },
    sectorListData: directSectorData,
    turnoverObjectiveData: directObjectiveTurnoverData!,
    currentTurnoverTriptychData: null,
    pastTurnoverTriptychData: null,
    isLoadingSector: !directSectorData,
    isLoadingTurnover: false,
    isLoadingTurnoverByHour: !lastWeekturnoverByHourData || !directTurnoverByHourData,
    hasErrorSector:
      !!directSectorError ||
      directSectorData === null ||
      !directSectorData?.[0]?.item?.length,
    hasErrorTurnover: false,
    hasErrorTurnoverByHour:
      !!lastWeekturnoverByHourError ||
      lastWeekturnoverByHourData === null ||
      (lastWeekturnoverByHourData && !lastWeekturnoverByHourData?.length) ||
      !!directTurnoverByHourError ||
      directTurnoverByHourData === null,
    hasErrorComparative: false,
    openInformationModal: () => null
  };

  return (
    <CommercialLayout>
      {displayLegacyUi ? (
        <CommercialContentPage {...contentPageProps} />
      ) : (
        <CommercialContentPageDesktop {...contentPageProps} />
      )}
    </CommercialLayout>
  );
}

export default Live;
